'use client';

import { useMemo, useState } from 'react';
import { ArrowUpDown, AlertCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SmartCell } from './smart-cell';
import { JsonViewer } from './json-viewer';
import { humanizeKey } from '@/lib/integrations/utils';
import type { ColumnConfig } from '@/lib/integrations/types';

interface GenericJsonTableProps {
  data: Record<string, unknown>[];
  title?: string;
  loading?: boolean;
  error?: Error | null;
  columns?: ColumnConfig[];
  onColumnsChange?: (columns: ColumnConfig[]) => void;
  onRowClick?: (row: Record<string, unknown>, index: number) => void;
}

export function GenericJsonTable({
  data,
  loading,
  error,
  columns: externalColumns,
  onColumnsChange,
  onRowClick,
}: GenericJsonTableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedValue, setExpandedValue] = useState<unknown>(null);

  // Auto-detect columns from data if not provided
  const columns = useMemo(() => {
    if (externalColumns && externalColumns.length > 0) {
      return externalColumns;
    }
    if (!data.length) return [];

    return Object.keys(data[0]).map((key, index) => ({
      key,
      label: humanizeKey(key),
      visible: true,
      order: index,
    }));
  }, [data, externalColumns]);

  // Filter visible columns
  const visibleColumns = useMemo(
    () => columns.filter((c) => c.visible).sort((a, b) => a.order - b.order),
    [columns]
  );

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortKey) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      return 0;
    });
  }, [data, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <AlertCircle className="size-10 text-destructive mb-2" />
        <h3 className="font-semibold">Failed to load data</h3>
        <p className="text-muted-foreground text-sm">{error.message}</p>
      </div>
    );
  }

  // Empty state
  if (!data.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <h3 className="font-semibold">No data available</h3>
        <p className="text-muted-foreground text-sm">
          Try adjusting your filters or connect a different integration.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableHead key={column.key} className="whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-3 h-8 font-semibold"
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                    <ArrowUpDown className="ml-2 size-3" />
                  </Button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                onClick={() => onRowClick?.(row, rowIndex)}
              >
                {visibleColumns.map((column) => (
                  <TableCell key={column.key} className="max-w-xs">
                    <SmartCell
                      value={row[column.key]}
                      maxLength={80}
                      onExpandObject={setExpandedValue}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Expanded JSON Dialog */}
      <Dialog open={expandedValue !== null} onOpenChange={() => setExpandedValue(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>JSON Data</DialogTitle>
          </DialogHeader>
          <JsonViewer data={expandedValue} collapsed={false} />
        </DialogContent>
      </Dialog>
    </>
  );
}
